/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types */
import type { User } from "lib/apollo/types"
import { calculateAverageOrder } from "utils/calculateAverageOrder"
import { getOneTrustDataLayer } from "utils/getOneTrustDataLayer"
import { getOriginalLocation } from "utils/getOriginalLocation"
import { mapHotJarMembershipTerm } from "utils/mapHotJarMembershipTerm"
import { pathnameToPageType } from "utils/pathnameToPageType"
import { GTM_CONTAINER_ID } from "./constants"
import { defaultData } from "./dataLayerDefaults"
import { removeDeprecatedDataLayerVars } from "./removeDeprecatedDataLayerVars"
import type {
  DataLayer,
  DataLayerEvent,
  DimensionValue,
  DimensionReducer,
  DimensionKey,
  PII,
} from "./types"
import { PIIDefaults } from "./pIIDefaults"

const isProd = process.env.NODE_ENV === "production"

const env = isProd ? "prod" : "dev"

const DATA_LAYER = `bp-data-layer-${env}`

export const reducerMap = new Map<DimensionKey, DimensionReducer>([
  ["store", (locale = "UK") => `BeautyPie ${locale.toUpperCase()}`],
  ["member_id", (id: string | undefined): string => id ?? ""],
])

/**
 * Updates a specific dataLayer dimension
 * @param dimension DataLayer key
 * @param value Value to update
 * @returns
 */
export const pushToDataLayer = (
  dimension: DimensionKey,
  value: DimensionValue
): DimensionValue => {
  const reducer = reducerMap.get(dimension)
  const val = reducer?.call(null, value) ?? value

  const data = setData(dimension, val)
  window.dataLayer?.push(data)

  return val
}

/**
 * Sends the specific dataLayer event
 * @param event DataLayerEvent enum
 * @param value Event value to push
 * @returns
 */

export const eventToDataLayer = (event: DataLayerEvent, value?: any): void => {
  window.dataLayer?.push({
    event,
    ...value,
  })
}

/**
 * @param locale
 * @returns
 */
type DataLayerDefaultParams = {
  locale?: string
  asPath?: string
  user?: User
  event_id?: string
  categories?: {
    category?: string
    subCategory?: string
    subSubCategory?: string
  }
  hashedEmail?: string
  is404?: boolean
  PII?: PII
}

export const dataLayerDefaults = ({
  locale = "uk",
  asPath = "",
  user,
  event_id,
  categories,
  hashedEmail,
  is404 = false,
  PII = PIIDefaults,
}: DataLayerDefaultParams): DataLayer => {
  const isGuest = !user?.isLoggedIn
  const emailAddress = user?.email ?? ""

  const localData = {
    ...defaultData,
    ...removeDeprecatedDataLayerVars(getData()),
    event_id: event_id ?? "",
    version: process.env.NEXT_PUBLIC_GIT_COMMIT_SHA,
    pageType: is404 ? "Error Page" : pathnameToPageType(asPath, locale),
    membership_status: user?.isMember ? "Active" : "Inactive",
    member_id: isGuest ? "" : user.id,
    store: `BeautyPie ${locale.toUpperCase()}`,
    email_address: isGuest ? "" : emailAddress,
    OnetrustActiveGroups: getOneTrustDataLayer(),
    date_of_birth: user?.dateOfBirth?.toString() ?? "",
    RegistrationDate: user?.registrationDateTime?.toString() ?? "",
    MembershipStartDate: user?.MembershipStartDate?.toString() ?? "",
    MembershipEndDate: user?.MembershipEndDate?.toString() ?? "",
    hotjar_membership_term: isGuest
      ? ""
      : mapHotJarMembershipTerm(
          user?.membership?.id,
          user?.membership?.duration
        ),
    TotalOrders: user?.TotalOrders?.toString() ?? "",
    average_order_value: isGuest
      ? ""
      : calculateAverageOrder(user?.LifetimeValueOrders, user?.TotalOrders),
    hashed_email_address: !isGuest ? hashedEmail : "",
    originalLocation: getOriginalLocation(),
    ...PII,
  }
  if (categories) {
    localData.category = categories.category
    localData.subCategory = categories.subCategory
    localData.subSubCategory = categories.subSubCategory
  } else {
    localData.category = undefined
    localData.subCategory = undefined
    localData.subSubCategory = undefined
  }
  const entries = Array.from(reducerMap.entries())

  const data: DataLayer = entries.reduce<Record<DimensionKey, any>>(
    (acc, [key, value]) => {
      if (typeof acc[key] === "undefined") {
        const isStore = key === "store"

        acc[key] = value(isStore ? locale : null)
      }

      return acc
    },
    localData
  )

  localStorage.setItem(DATA_LAYER, JSON.stringify(data))
  return data
}

/**
 * @returns
 */
export const getData = (): DataLayer => {
  const localData: string = localStorage.getItem(DATA_LAYER) ?? "{}"
  return JSON.parse(localData)
}

/**
 * @param key
 * @param value
 * @returns
 */
const setData = (
  key: string,
  value: string | number | undefined
): DataLayer => {
  const data: DataLayer = {
    ...removeDeprecatedDataLayerVars(getData()),
    [key]: value,
  }

  localStorage.setItem(DATA_LAYER, JSON.stringify(data))

  return data
}

/**
 * Reset everything in data layer
 * @returns
 */
export const resetDataLayer = (): void => {
  if (window.google_tag_manager) {
    if (window.dataLayer) window.dataLayer.length = 0
    const gtm = window.google_tag_manager[GTM_CONTAINER_ID]
    gtm.dataLayer.reset()
  }
}

/**
 * Reset a single key in data layer
 * @param key Data layer key to reset value
 * @returns
 */
export const resetDataLayerItem = (key: string): void => {
  if (window.google_tag_manager) {
    const gtm = window.google_tag_manager[GTM_CONTAINER_ID]
    if (gtm) gtm.dataLayer.set(key, undefined)
  }
}
